import React from "react";
import {
  CalendarDaysIcon,
  ClockIcon,
  EnvelopeIcon,
  PhoneIcon,
  UserCircleIcon,
  VideoCameraIcon,
  BuildingOfficeIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { labelAppointmentModality } from "../../utils/consultationModes";

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-MX", {
    timeZone: "America/Mexico_City",
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function formatTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("es-MX", {
    timeZone: "America/Mexico_City",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function statusLabel(status) {
  if (status === "scheduled") return "Programada";
  if (status === "cancelled") return "Cancelada";
  return status || "—";
}

function statusBadgeClass(status) {
  if (status === "scheduled") return "bg-emerald-50 text-emerald-800 border-emerald-200";
  if (status === "cancelled") return "bg-red-50 text-red-700 border-red-200";
  return "bg-brand-lilac/40 text-brand-navy border-brand-lavender/30";
}

function modalityBadgeClass(modality) {
  if (modality === "virtual") return "bg-blue-50 text-blue-700 border-blue-200";
  if (modality === "in_person") return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-brand-lilac/40 text-brand-navy border-brand-lavender/30";
}

function Row({ icon: Icon, label, value, href, hrefType }) {
  const content = href ? (
    <a
      href={href}
      className="text-brand-lavender hover:underline break-all"
    >
      {value}
    </a>
  ) : (
    <span className="break-words">{value || "—"}</span>
  );
  return (
    <div className="flex items-start gap-3">
      <Icon className="h-4 w-4 shrink-0 text-brand-gray mt-0.5" aria-hidden />
      <div className="min-w-0">
        <p className="portal-label mb-0.5">{label}</p>
        <p className="portal-value">{content}</p>
      </div>
    </div>
  );
}

/**
 * Drawer lateral con el detalle de una cita.
 * Props:
 *   open        boolean
 *   appt        object | null
 *   onClose     () => void
 *   onCancel    (appt) => void   — handler para cancelar la cita
 */
export default function AdminApptDetailDrawer({ open, appt, onClose, onCancel }) {
  if (!appt) return null;

  const dateStr = formatDate(appt.startUtc);
  const timeRange =
    appt.startUtc && appt.endUtc
      ? `${formatTime(appt.startUtc)} – ${formatTime(appt.endUtc)}`
      : formatTime(appt.startUtc);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/20 backdrop-blur-[1px] transition-opacity duration-300 ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
        aria-hidden
      />

      {/* Panel */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Detalle de cita"
        className={`fixed right-0 top-0 z-50 h-full w-full max-w-sm bg-white shadow-2xl
          flex flex-col transition-transform duration-300
          ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-brand-lavender/15 px-5 py-4">
          <div className="min-w-0">
            <p className="portal-label">Detalle de cita</p>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <span
                className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[0.75rem] font-semibold ${statusBadgeClass(appt.status)}`}
              >
                {statusLabel(appt.status)}
              </span>
              {appt.modality && (
                <span
                  className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[0.75rem] font-semibold ${modalityBadgeClass(appt.modality)}`}
                >
                  {labelAppointmentModality(appt.modality)}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-brand-gray hover:bg-brand-lilac/30 transition-colors"
            aria-label="Cerrar"
          >
            <XMarkIcon className="h-5 w-5" aria-hidden />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
          {/* Fecha y hora */}
          <section className="space-y-3">
            <h2 className="portal-section-title">Fecha y hora</h2>
            <Row icon={CalendarDaysIcon} label="Fecha" value={dateStr} />
            <Row icon={ClockIcon} label="Horario" value={timeRange} />
          </section>

          <div className="border-t border-brand-lavender/10" />

          {/* Cliente */}
          <section className="space-y-3">
            <h2 className="portal-section-title">Cliente</h2>
            <Row icon={UserCircleIcon} label="Nombre" value={appt.clientName} />
            {appt.clientEmail && (
              <Row
                icon={EnvelopeIcon}
                label="Correo"
                value={appt.clientEmail}
                href={`mailto:${appt.clientEmail}`}
              />
            )}
            {appt.clientPhone && (
              <Row
                icon={PhoneIcon}
                label="Teléfono"
                value={appt.clientPhone}
                href={`tel:${String(appt.clientPhone).replace(/\s/g, "")}`}
              />
            )}
          </section>

          <div className="border-t border-brand-lavender/10" />

          {/* Psicólogo */}
          <section className="space-y-3">
            <h2 className="portal-section-title">Psicólogo</h2>
            <Row icon={UserCircleIcon} label="Nombre" value={appt.psychologistName} />
            {appt.psychologistEmail && (
              <Row
                icon={EnvelopeIcon}
                label="Correo"
                value={appt.psychologistEmail}
                href={`mailto:${appt.psychologistEmail}`}
              />
            )}
            {appt.psychologistPhone && (
              <Row
                icon={PhoneIcon}
                label="Teléfono"
                value={appt.psychologistPhone}
                href={`tel:${String(appt.psychologistPhone).replace(/\s/g, "")}`}
              />
            )}
          </section>

          {appt.meetingLink && (
            <>
              <div className="border-t border-brand-lavender/10" />
              <section className="space-y-3">
                <h2 className="portal-section-title">Videollamada</h2>
                <Row
                  icon={VideoCameraIcon}
                  label="Enlace"
                  value={appt.meetingLink}
                  href={appt.meetingLink}
                />
              </section>
            </>
          )}

          {appt.location && (
            <>
              <div className="border-t border-brand-lavender/10" />
              <section className="space-y-3">
                <h2 className="portal-section-title">Lugar</h2>
                <Row icon={BuildingOfficeIcon} label="Dirección" value={appt.location} />
              </section>
            </>
          )}
        </div>

        {/* Footer con acción */}
        {appt.status === "scheduled" && (
          <div className="border-t border-brand-lavender/15 px-5 py-4">
            <button
              type="button"
              onClick={() => onCancel(appt)}
              className="w-full rounded-xl border border-red-300 py-2.5 text-[0.9375rem] font-semibold text-red-700 hover:bg-red-50 transition-colors"
            >
              Cancelar cita
            </button>
          </div>
        )}
      </aside>
    </>
  );
}
